import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';

import type { OmitStylesButtonHTMLAttributes } from '../../types';
import { cn } from '../../utils/cn';

export const buttonVariants = cva(
    'rocco-button group z-1 inline-flex cursor-pointer items-center justify-center overflow-hidden border border-transparent font-normal whitespace-nowrap ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            color: {
                primary:
                    'border-brick-50 bg-brick-50 text-primary-foreground hover:border-brick-200 hover:bg-brick-200 hover:text-primary-foreground',
                secondary:
                    'border-gray-100 bg-gray-100 text-secondary-foreground hover:border-gray-100 hover:bg-gray-200',
                'header-search-button':
                    'bg-[#d6d6d6] fill-[#323232] text-[#323232] hover:bg-[#c6c6c6]',
                destructive: 'bg-destructive text-destructive-foreground',
                muted: 'bg-gray-900 text-gray-50',
                signUp: 'min-w-[130px] bg-gray-100 text-secondary-foreground hover:bg-gray-200',
                transparent: 'bg-transparent fill-gray-900 text-gray-900',
                white: 'border-white bg-white fill-black text-black hover:border-gray-50/70 hover:bg-gray-50/70',
                dark: 'bg-black fill-white text-white hover:bg-gray-700',
                gray: 'bg-[#5B5D5E] fill-white text-white hover:bg-gray-700',
                lightGray: 'bg-[#E8E8E8] fill-gray-900 text-black',
                grayDark: 'bg-[#232323] fill-[#232323] text-white',
                outline:
                    'border border-gray-900 bg-transparent fill-gray-900 text-gray-900',
                outlineBM:
                    'border border-gray-900 bg-transparent fill-gray-900 text-gray-900 hover:bg-black hover:fill-white hover:text-white [&_svg]:hover:text-white',
                glassy: 'border bg-[rgba(20,20,20,0.18)] fill-gray-900 tracking-[0.32px] text-gray-900 backdrop-blur-island md:hover:bg-gray-900 md:hover:fill-white md:hover:text-white',
                secondary70:
                    'bg-gray-50/70 text-secondary-foreground backdrop-blur-island hover:border-gray-50 hover:bg-gray-50',
                'primary-bm': 'bg-[#D87F5C]',
            },
            inverted: {
                true: '',
                false: '',
            },
            size: {
                default: 'px-6 py-2', //  42px
                larger: 'px-8 py-2.5', // 46.8px
                xLarge: 'px-8 py-4.5', // 58px
                badge: 'px-4 py-2',
                narrow: 'px-3 py-1',
                compact: 'px-0 py-2',
                square18: 'size-4.5',
                square24: 'size-6',
                square36: 'size-9', // 36px
                square40: 'size-10', // 40px
                square42: 'size-10.5', // 42px
                square44: 'size-11', // 44px
                square50: 'size-12.5', // 50px
                square54: 'size-13.5', // 54px
                square64: 'size-16',
                square72: 'size-18',
                square80: 'size-20',
                square112: 'size-28',
                icon: 'size-8 p-0',
                headerSearch: 'size-13.5 p-1', // 54px with minimal padding for icons
                headerSearchSmall: 'size-12.5 p-1', // slightly smaller variant for Dec 2025 products page topnav redesign
            },
            shape: {
                default: 'rounded-full',
                squareRounded: 'rounded-2',
                squareRounded1: 'rounded-1',
                square: 'rounded-none',
                squareRoundedMini: 'rounded-1',
            },
            border: {
                default: '',
                white: 'border border-white',
                dark: 'border border-gray-900',
            },
            layoutMode: {
                default: '',
                fillFlex: 'flex-1',
                noShrink: 'shrink-0',
            },
            minWidth: {
                default: '',
                '120': 'min-w-30',
                '170': 'min-w-42.5',
                '180': 'min-w-45',
                '200': 'min-w-50',
            },
            weight: {
                default: 'font-medium',
                normal: 'font-normal',
            },
            overflow: {
                default: '',
                visible: 'overflow-visible',
            },
            /** When true, disables hover effects. Set --rocco-btn-bg, --rocco-btn-border, --rocco-btn-text, --rocco-btn-fill to match base styles. */
            noHover: {
                true: '[&:hover]:border-(--rocco-btn-border)! [&:hover]:bg-(--rocco-btn-bg)! [&:hover]:fill-(--rocco-btn-fill)! [&:hover]:text-(--rocco-btn-text)!',
                false: '',
            },
            textSize: {
                default: 'text-base',
                small: 'text-sm',
                xSmall: 'text-xs',
                large: 'text-lg',
                xLarge: 'text-xl',
            },
        },
        compoundVariants: [
            {
                color: 'primary',
                inverted: true,
                class: 'border-primary-foreground bg-primary-foreground text-brick-50',
            },
            {
                color: 'secondary',
                inverted: true,
                class: 'border-gray-900 bg-gray-900 text-gray-100 hover:border-gray-700 hover:bg-gray-700',
            },
            {
                color: 'destructive',
                inverted: true,
                class: 'bg-destructive-foreground text-destructive',
            },
            {
                color: 'muted',
                inverted: true,
                class: 'bg-gray-50 text-gray-900',
            },
            {
                color: 'signUp',
                inverted: true,
                class: 'bg-white/80 fill-black text-black hover:bg-white/60',
            },
            {
                color: 'transparent',
                inverted: true,
                class: 'fill-white text-white',
            },
            {
                color: 'outline',
                inverted: true,
                class: 'border-white fill-white text-white',
            },
            {
                color: 'glassy',
                inverted: true,
                class: 'fill-white text-white',
            },
            {
                color: 'primary-bm',
                noHover: false,
                class: 'hover:bg-[#D87F5C]/80',
            },
            {
                color: 'primary-bm',
                noHover: true,
                class: 'hover:bg-[#D87F5C]!',
            },
            {
                color: 'outline',
                noHover: true,
                class: 'hover:border-gray-900! hover:bg-transparent! hover:fill-gray-900! hover:text-gray-900!',
            },
            {
                color: 'dark',
                noHover: true,
                class: 'hover:border-transparent! hover:bg-black! hover:fill-white! hover:text-white!',
            },
            {
                color: 'outlineBM',
                noHover: true,
                class: 'hover:border-gray-900! hover:bg-transparent! hover:fill-gray-900! hover:text-gray-900! [&_svg]:hover:text-gray-900!',
            },
        ],
        defaultVariants: {
            color: 'primary',
            size: 'default',
            shape: 'default',
            inverted: false,
            layoutMode: 'default',
            minWidth: 'default',
            weight: 'default',
            overflow: 'default',
            textSize: 'default',
            noHover: false,
        },
    },
);

export interface ButtonProps
    extends
        OmitStylesButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    loadingIconClassName?: string;
    asChild?: boolean;
    isLoading?: boolean;
    clickAnimation?: 'scale' | false;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (props, ref) => {
        const {
            children,
            color,
            size,
            shape,
            inverted,
            layoutMode,
            className,
            loadingIconClassName,
            asChild = false,
            isLoading,
            minWidth,
            weight,
            overflow,
            clickAnimation = false,
            textSize,
            noHover,
            ...rest
        } = props;

        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                className={cn(
                    buttonVariants({
                        color,
                        size,
                        shape,
                        inverted,
                        layoutMode,
                        minWidth,
                        weight,
                        overflow,
                        textSize,
                        noHover,
                    }),
                    clickAnimation === 'scale' &&
                        'origin-center transform-gpu transition-transform duration-100 active:scale-95',
                    className,
                )}
                ref={ref}
                {...rest}
            >
                {isLoading !== undefined ? (
                    <div className="relative items-center justify-items-center">
                        <div className="absolute flex h-full w-full items-center justify-center">
                            <LoaderCircle
                                className={cn(
                                    'animate-spin',
                                    isLoading ? 'visible' : 'invisible',
                                    loadingIconClassName,
                                )}
                            />
                        </div>
                        <div
                            className={cn(
                                'flex items-center justify-center',
                                isLoading ? 'invisible' : 'visible',
                            )}
                        >
                            {children}
                        </div>
                    </div>
                ) : (
                    children
                )}
            </Comp>
        );
    },
);

Button.displayName = 'Button';
